import type { ExperienceDraft } from '@/lib/experience-draft';
import { draftAsContent } from '@/lib/experience-draft';
import { entryOf, newLocalId, withPage } from '@/lib/page-editor';
import type {
  ExperienceBlockDraft,
  ExperiencePageDraft,
  TempsDraft,
} from '@matter_extractor/shared';
import {
  EXPERIENCE_BLOCKS_MAX,
  EXPERIENCE_PAGES_MAX,
  EXPERIENCE_PAGE_NAME_MAX,
  EXPERIENCE_VARIABLE_NAME_MAX,
  cloneExperienceBlock,
  cloneExperiencePage,
  isFieldBlock,
  namedVariablesOf,
  nextFreeCopyName,
  nextFreeVariableName,
  pageChoiceName,
} from '@matter_extractor/shared';

/**
 * Dupliquer une page et dupliquer un bloc (refonte modulaire §4.2, grill du
 * 2026-09-21 ; issue #118).
 *
 * The clone and the two name calculations are `shared`'s (issue #115). What is
 * here is the COMPOSITION §4.2 leaves to the caller: mint, name, suffix the
 * variables, and put the copy just after its source.
 *
 * ## Why a module of its own, and not two additions to two files
 *
 * A page gesture belongs in `experience-draft.ts`, beside `insertPageAt` and
 * `removePage`; a block gesture belongs in `page-editor.ts`, beside `addBlock`.
 * But the two share the one rule the clone deliberately does NOT carry - « le
 * nom de variable est suffixé automatiquement » - and `experience-draft.ts` is
 * UPSTREAM of `page-editor.ts`, so the rule cannot live in the second and be
 * read by the first without a cycle. Written in both, it would be two contracts
 * for one sentence of §8.6, which is the drift this repository keeps naming.
 * So both gestures live here, above both modules, and neither of them grows a
 * half of the rule.
 *
 * ## Ce que le réadressage ne fait PAS, et c'est un cadeau de H60
 *
 * Nothing. A page can never cite its own field or its own choice - « jamais sur
 * sa propre page, dont la valeur n'existe pas tant qu'elle s'affiche » (§8.6,
 * H60) - so every `{{champ:...}}` and `{{choix:...}}` a legal page carries names
 * a page EARLIER in the timeline, outside the subtree. The copy landing just
 * after its source, all of it stays in reach: no `variable-out-of-scope` is
 * possible by construction. `experience-duplicate.test.ts` PROVES it on the
 * whole gesture, minting included, rather than assuming it.
 *
 * ## Le nom de variable, lui, n'est pas une adresse
 *
 * It is « QU'UN LIBELLÉ : ni une clé, ni une adresse » (§8.6), unique in the
 * timeline and checked at « Enregistrer une nouvelle version ». A copied Input
 * would therefore arrive with a name the save refuses, and on a page carrying
 * four of them the four red fields are scattered down the stack and discovered
 * only at the save. So they are suffixed here, « email » -> « email 2 », each
 * result added to the taken list before the next is asked for.
 *
 * ## Le Bouton : renommé quand la PAGE est copiée, jamais quand LUI l'est
 *
 * This is the one place the two gestures part, and §8.6 is why: « le choix
 * appartient à la page, pas au bouton » - a page retains ONE choice, « une
 * seule par page », and every remembering button of that page carries the SAME
 * name. They are the issues of one choice, not two variables.
 *
 * - duplicating a BLOCK that is a button adds a third issue to a choice that
 *   already exists. Renaming it would not create a variable, it would create a
 *   dead label: `pageChoiceName` reads the FIRST named remembering button, so
 *   the new name would be a string no `{{choix:...}}` can reach and
 *   `namedVariablesOf` never lists. Its name is copied verbatim, which is what
 *   the comment of `cloneExperienceBlock` asks of its caller;
 * - duplicating a PAGE creates a SECOND choice, at a second address, and the
 *   copy declaring its source's label is a genuine duplicate that
 *   `duplicateVariableNames` flags and the save refuses. So the CHOICE is
 *   suffixed - once, and written onto every remembering button of the copy, so
 *   that « une seule par page » still holds on the other side of the gesture.
 *
 * The acceptance criterion of #118 names the Bouton among the suffixed and the
 * comment of #115 excludes it; both are right about the gesture each describes,
 * and the split above is what makes them one rule.
 *
 * ## « Nom de la personne dans Sessions » ne se copie pas (§9.2, H75)
 *
 * « Au plus une par expérience » (`person-name-repeated`): the SOURCE keeps the
 * box, so a copy carrying it too would be born holding the refusal this module
 * exists to avoid - the consent page of the template, duplicated once, would
 * otherwise be unsavable. Nothing to do HERE: the clone of `shared` already
 * brings every copied Input back with the box unticked (`remapBlock`), and
 * `experience-duplicate.test.ts` holds it on both gestures, minting included.
 */

/**
 * Whether the gesture is OFFERED on this page at all.
 *
 * Never on the protocol page: §9.1 wants it « obligatoire, unique », and a
 * second one « est un état que §9.5 ne sait pas lire ». The refusal is the icon
 * being ABSENT and not disabled, because a disabled icon promises a ceiling one
 * could come back under, and this one never lifts. The consent page is an
 * ordinary `content` page and duplicates like any other.
 *
 * `cloneExperiencePage` would copy a protocol page faithfully if it were given
 * one - it copies the `kind` it receives - so this is the screen's half of that
 * arbitration, stated once here for both screens that draw the icon.
 */
export function pageOffersDuplicate(page: ExperiencePageDraft): boolean {
  return page.kind === 'content';
}

/**
 * Why the page's copy icon is disabled, or null when it is live (§4.2).
 *
 * « Au plafond de la liste, l'icône est désactivée et dit pourquoi au survol :
 * un geste sans boîte n'a que cet endroit-là pour refuser. » The sentence is
 * the tooltip verbatim, so the reason reaches the operator before the click
 * rather than as a gesture that appears to do nothing.
 */
export function pageDuplicateRefusal(draft: ExperienceDraft): string | null {
  if (draft.pages.length < EXPERIENCE_PAGES_MAX) return null;
  return `L’expérience porte déjà ${EXPERIENCE_PAGES_MAX} pages, le maximum : supprime-en une pour en dupliquer une autre.`;
}

/**
 * Why a TEMPS refuses one more block, or null (§4.2, §9.3).
 *
 * The ceiling is counted on the temps and not on the page, because that is
 * where `experienceContentSchema` puts it - `.max(EXPERIENCE_BLOCKS_MAX)` on a
 * temps' `blocks` - and where `addBlock` already refuses. A copy joining « le
 * temps de sa source » is therefore bounded by exactly the array the schema
 * bounds, and a block of a full temps can still be duplicated once it has moved.
 *
 * It takes the TEMPS rather than a block id so the stack can ask ONCE per temps
 * while drawing up to twenty rows under it; `blockDuplicateRefusal` is the same
 * question asked by a caller that holds only an id.
 */
export function tempsDuplicateRefusal(temps: TempsDraft): string | null {
  if (temps.blocks.length < EXPERIENCE_BLOCKS_MAX) return null;
  return `Ce temps porte déjà ${EXPERIENCE_BLOCKS_MAX} blocs, le maximum : la copie rejoint le temps de sa source.`;
}

/** Why a block's copy icon is closed, or null - `tempsDuplicateRefusal` by id. */
export function blockDuplicateRefusal(page: ExperiencePageDraft, blockId: string): string | null {
  const host = page.temps.find((temps) => temps.blocks.some((block) => block.id === blockId));
  return host === undefined ? null : tempsDuplicateRefusal(host);
}

/**
 * Every variable name the timeline declares today - what a suffix is free OF.
 *
 * TRIMMED, and that is not tidiness: `experienceBlockSchema` declares
 * `variableName` as `z.string().trim()`, so the name that reaches the dry run
 * and the store is trimmed while the DRAFT holds exactly what was typed
 * (`variable-name-field.tsx` passes `event.currentTarget.value` through). A
 * timeline holding « email 2 » with a stray space would otherwise look free
 * here and collide at the save - the one refusal this module exists to avoid.
 */
function takenVariableNames(draft: ExperienceDraft): string[] {
  return namedVariablesOf(draftAsContent(draft)).map((named) => named.name.trim());
}

/**
 * One block's variable name suffixed, and `held` grown by what it took.
 *
 * `held` is the caller's list and is APPENDED TO, which is the whole point:
 * two Inputs both called « email » would otherwise both be offered « email 2 »,
 * and the copy would be born holding the duplicate the suffix exists to avoid.
 *
 * A block with no variable name stays without one - an empty name declares
 * nothing (`namedVariablesOf` skips it), so a copy must not gain a name the
 * operator never wrote - and a BUTTON is left alone here, for the reason the
 * head of this module gives. The Sélection and the Note are renamed like an
 * Input (#176): « pertinence » copied is « pertinence 2 ».
 */
function renamedField(block: ExperienceBlockDraft, held: string[]): ExperienceBlockDraft {
  // The four blocks that declare a field since #176: an Input, a Checkbox, a
  // Sélection and a Note (`isFieldBlock`) - a copied Note arrives with a name
  // the save would refuse exactly as a copied Input does.
  if (!isFieldBlock(block)) return block;
  // TRIMMED, for the reason `takenVariableNames` gives: a name of three spaces
  // declares nothing once the schema has trimmed it, so it is no name at all.
  if (block.variableName.trim() === '') return block;
  const name = nextFreeVariableName(block.variableName, held, EXPERIENCE_VARIABLE_NAME_MAX);
  held.push(name);
  return { ...block, variableName: name };
}

/** Every Input and Checkbox of the copy renamed, in reading order (see above). */
/**
 * Le nom d'un bloc COLLÉ : suffixé seulement s'il entre en collision (#136).
 *
 * `renamedField` suffixe TOUJOURS, et c'est juste pour une duplication - la
 * source garde « email », donc la copie ne peut pas le garder aussi. Un collage
 * vient d'ailleurs : la timeline d'ici n'a souvent aucun « email », et le
 * suffixer quand même donnerait un « email 2 » sans « email », que l'opérateur
 * devrait renommer à la main à chaque collage entre expériences.
 */
function renamedIfTaken(block: ExperienceBlockDraft, held: string[]): ExperienceBlockDraft {
  if (!isFieldBlock(block)) return block;
  // TRIMMED, pour la raison que `takenVariableNames` donne juste au-dessus.
  const name = block.variableName.trim();
  if (name === '' || !held.includes(name)) return block;
  return renamedField(block, held);
}

function renameFields(page: ExperiencePageDraft, held: string[]): ExperiencePageDraft {
  return {
    ...page,
    temps: page.temps.map((temps) => ({
      ...temps,
      blocks: temps.blocks.map((block) => renamedField(block, held)),
    })),
  };
}

/**
 * The copy's CHOICE renamed - once for the page, onto every button that holds
 * it (§8.6, see the head).
 *
 * `pageChoiceName` is asked rather than the buttons being read here, so what is
 * rewritten is exactly the name the timeline counts. Null when the page writes
 * no choice - fewer than two buttons, or none of them remembering - and then
 * nothing is touched.
 *
 * It runs AFTER the fields and against the list they grew, so a choice cannot
 * be handed the name an Input of the same copy has just taken. It records its
 * own result in `held` too, which nothing reads today: the list is meant to say
 * what is taken, and a step added after this one has to start from the truth
 * rather than from a list that stopped being maintained one line early.
 *
 * Only the buttons carrying THE CHOICE'S OWN NAME are rewritten, never every
 * remembering button. §8.6 says they all carry the same name, but the inspector
 * draws one field per button and does not enforce it, so a page can legitimately
 * hold « parcours » on one and « parcours-bis » on another. `pageChoiceName`
 * elects the first; rewriting the second would delete a name somebody wrote -
 * the mirror of the rule this module states about never inventing one.
 */
function renameChoice(page: ExperiencePageDraft, held: string[]): ExperiencePageDraft {
  const choice = pageChoiceName(page);
  if (choice === null) return page;
  const name = nextFreeVariableName(choice, held, EXPERIENCE_VARIABLE_NAME_MAX);
  held.push(name);
  return {
    ...page,
    temps: page.temps.map((temps) => ({
      ...temps,
      blocks: temps.blocks.map((block) =>
        block.type === 'button' && block.rememberChoice && block.variableName.trim() === choice
          ? { ...block, variableName: name }
          : block,
      ),
    })),
  };
}

/** What the page gesture answers: the draft, and what to select - null when refused. */
export type PageDuplicated = {
  readonly draft: ExperienceDraft;
  readonly pageId: string | null;
};

/**
 * « Dupliquer cette page » - the copy just after its source, selected (§4.2).
 *
 * Three steps, each answered by something that already exists:
 * `cloneExperiencePage` mints the page, its temps and its blocks; the two
 * renamers above suffix what §8.6 makes a label rather than an address;
 * `nextFreeCopyName` names the page against the names the timeline already
 * shows side by side - the source's included, which is the point, since
 * duplicating « Accueil » has to give « Accueil (copie) » and not « Accueil »
 * twice.
 *
 * Item names are not unique and homonyms are tolerated (§4.2, §7.2); the suffix
 * is there because « un jumeau non édité dans la palette `@` est un piège ».
 */
export function duplicatePage(draft: ExperienceDraft, pageId: string): PageDuplicated {
  const at = draft.pages.findIndex((one) => one.id === pageId);
  const source = at === -1 ? undefined : draft.pages[at];
  if (source === undefined || !pageOffersDuplicate(source)) return { draft, pageId: null };
  if (pageDuplicateRefusal(draft) !== null) return { draft, pageId: null };

  const { clone } = cloneExperiencePage(source, newLocalId);
  const held = takenVariableNames(draft);
  const named: ExperiencePageDraft = {
    ...renameChoice(renameFields(clone, held), held),
    name: nextFreeCopyName(
      source.name,
      draft.pages.map((one) => one.name),
      EXPERIENCE_PAGE_NAME_MAX,
    ),
  };

  const pages = [...draft.pages];
  pages.splice(at + 1, 0, named);
  return { draft: { ...draft, pages }, pageId: named.id };
}

/** What the block gesture answers. */
export type BlockDuplicated = {
  readonly draft: ExperienceDraft;
  readonly blockId: string | null;
};

/**
 * « Dupliquer ce bloc » - the copy just after its source, in the temps of its
 * source, selected (§4.2, §9.3).
 *
 * It takes the DRAFT and not the page, for one reason: the names a variable
 * suffix must be free of are the whole TIMELINE's (§8.6), and a page does not
 * hold the timeline. `withPage` puts the rewritten page back, so that line
 * stays the only thing here that knows an experience exists.
 *
 * A block carries no name, so there is no « (copie) » to append - « rien sur
 * ceux qui n'en portent pas » (§4.2). Its content, its visual and its entry
 * animation are copied as they stand.
 *
 * ## Le cas du bouton SEUL, qui est le seul endroit où ce geste nomme
 *
 * The head of this module says a duplicated button is never renamed, and that
 * holds while the page already writes a choice. It does NOT hold when the page
 * had only ONE button: « un bouton seul n'écrit rien » (§8.6), so
 * `pageChoiceName` answered null and the timeline declared nothing. The copy
 * makes it two, and the page starts declaring a choice THE GESTURE CREATED -
 * which can collide with a name another page already carries, producing exactly
 * the `duplicate-variable-name` this module exists to prevent.
 *
 * So the choice is suffixed in that one case, and only when it actually
 * collides: the name belongs to the PAGE, so both buttons take the new one -
 * including the source's, which is not a copy being renamed but the page's one
 * choice being given a free name. A collision-free birth is left alone, because
 * the label is then exactly what the operator typed and « renommer une variable
 * est gratuit » is their call to make, not this function's.
 */
export function duplicateBlock(
  draft: ExperienceDraft,
  pageId: string,
  blockId: string,
): BlockDuplicated {
  const page = draft.pages.find((one) => one.id === pageId);
  if (page === undefined) return { draft, blockId: null };
  const entry = entryOf(page, blockId);
  if (entry === null) return { draft, blockId: null };
  const host = page.temps[entry.tempsIndex];
  if (host === undefined) return { draft, blockId: null };
  if (tempsDuplicateRefusal(host) !== null) return { draft, blockId: null };

  const held = takenVariableNames(draft);
  const { clone } = cloneExperienceBlock(entry.block, newLocalId);
  const renamed = renamedField(clone, held);

  const grown: ExperiencePageDraft = {
    ...page,
    temps: page.temps.map((temps, index) =>
      index !== entry.tempsIndex
        ? temps
        : {
            ...temps,
            blocks: [
              ...temps.blocks.slice(0, entry.index + 1),
              renamed,
              ...temps.blocks.slice(entry.index + 1),
            ],
          },
    ),
  };

  /* Le choix que CE geste vient de faire naître, s'il en fait naître un. */
  const born = pageChoiceName(page) === null ? pageChoiceName(grown) : null;
  const settled = born !== null && held.includes(born) ? renameChoice(grown, held) : grown;

  return { draft: withPage(draft, pageId, () => settled), blockId: renamed.id };
}

/**
 * Coller un bloc venu du presse-papier, à la fin d'un temps (§9.3 ; grill du
 * 2026-09-23 ; issue #136).
 *
 * ## Pourquoi ici, et pas dans `page-editor.ts` à côté de `addBlock`
 *
 * Un collage est un `duplicateBlock` dont la SOURCE est ailleurs : même frappe
 * d'identifiants, même suffixe de nom de variable contre la timeline entière,
 * même refus au plafond du temps. Écrit là-bas, il aurait fallu y recopier les
 * trois, et c'est exactement la dérive que la tête de ce module nomme - « écrite
 * dans les deux, elle serait deux contrats pour une phrase de §8.6 ». Le bloc
 * arrive donc ici, le module qui tient déjà cette règle, et `duplicateBlock`
 * et lui ne diffèrent que par d'où vient le bloc et par où il atterrit.
 *
 * ## Le bloc est REFRAPPÉ, toujours, même s'il vient d'une autre expérience
 *
 * `cloneExperienceBlock` lui donne un identifiant neuf et réadresse les jetons
 * qui le citaient LUI - le cas d'un bloc copié puis collé sur la même page, où
 * un identifiant conservé ferait deux blocs à une seule adresse, ce que le
 * schéma refuse (`refuseDuplicateIds`). Le nom de variable, lui, n'est suffixé
 * QUE s'il entre en collision (`renamedIfTaken`) : « email » collé deux fois
 * dans une page donne « email » puis « email 2 », et un « email » collé dans
 * une expérience qui n'en a aucun reste « email ».
 *
 * Ce que le collage ne répare PAS : les jetons que le bloc collé porte vers
 * d'AUTRES blocs. Ils nomment des identifiants de son expérience d'origine, qui
 * n'existent pas ici, et `experienceProblems` les relève déjà comme des
 * variables pendantes à l'enregistrement. Les inventer serait pire : une
 * adresse fabriquée pointerait un bloc au hasard.
 *
 * À la FIN du temps, et non au rang de la source : le rang d'une page n'a pas
 * de sens sur une autre, et « à la suite » est le seul endroit que les deux
 * collages - même page, autre page - peuvent nommer pareil.
 */
export function pasteBlock(
  draft: ExperienceDraft,
  pageId: string,
  tempsId: string,
  block: ExperienceBlockDraft,
): BlockDuplicated {
  const page = draft.pages.find((one) => one.id === pageId);
  if (page === undefined) return { draft, blockId: null };
  const at = page.temps.findIndex((one) => one.id === tempsId);
  const host = page.temps[at];
  if (host === undefined) return { draft, blockId: null };
  if (tempsDuplicateRefusal(host) !== null) return { draft, blockId: null };

  const held = takenVariableNames(draft);
  const { clone } = cloneExperienceBlock(block, newLocalId);
  const renamed = renamedIfTaken(clone, held);

  const grown: ExperiencePageDraft = {
    ...page,
    temps: page.temps.map((temps, index) =>
      index !== at ? temps : { ...temps, blocks: [...temps.blocks, renamed] },
    ),
  };

  /* Le choix que CE collage vient de faire naître, s'il en fait naître un -
     `duplicateBlock` juste au-dessus donne la raison, mot pour mot. */
  const born = pageChoiceName(page) === null ? pageChoiceName(grown) : null;
  const settled = born !== null && held.includes(born) ? renameChoice(grown, held) : grown;

  return { draft: withPage(draft, pageId, () => settled), blockId: renamed.id };
}
